import orderBy from 'lodash/orderBy';
import type {
  FormTemplate,
  FormTemplateUpload,
  SessionFormat,
  SessionFormatCreateRequest,
  SessionFormatReorder
} from '~/api/generated';
import type { Locales } from '~/utils/constants/common';

export const useFormatStore = defineStore('format', {
  state: () => ({
    loading: false,
    formats: [] as SessionFormat[]
  }),
  getters: {
    getFormatById:
      (state) =>
      (id: SessionFormat['id']): SessionFormat | null =>
        state.formats.find((format) => format.id === id) || null,
    getFormatByExternalId:
      (state) =>
      (externalId: SessionFormat['external_id']): SessionFormat | null =>
        state.formats.find((format) => format.external_id === externalId) || null,
    orderedFormats:
      (state) =>
      (locale: Locales): SessionFormat[] =>
        orderBy(state.formats, ['is_custom', 'company.name', `name.${locale}`], ['desc', 'asc', 'asc']),
    visibleFormats: (state): SessionFormat[] => state.formats.filter((format) => !format.is_hidden)
  },
  actions: {
    async getFormats() {
      const apiStore = useApiStore();

      this.loading = true;

      try {
        const formats = await apiStore.api.sessionFormats.sessionFormatList();

        if (formats) {
          this.formats = formats.map(({ name: { de, en, fr, it }, ...format }) => ({
            ...format,
            name: {
              de,
              en: en || de,
              fr: fr || de,
              it: it || de
            }
          }));
        }
      } finally {
        this.loading = false;
      }
    },

    createFormat({ requestBody }: { requestBody: SessionFormatCreateRequest }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatCreate({ requestBody });
    },

    createFormatFormTemplate({ requestBody }: { requestBody: FormTemplateUpload }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatFormTemplateCreate({
        sessionFormatId: 1, // dummy id since the endpoint requires it, but it's not used for PDF uploads
        requestBody
      });
    },

    getFormatFormTemplate({
      formTemplateId,
      sessionFormatId
    }: {
      formTemplateId: FormTemplate['id'];
      sessionFormatId: SessionFormat['id'];
    }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatFormTemplateRetrieve({
        id: formTemplateId,
        sessionFormatId
      });
    },

    updateFormat({
      sessionFormatId,
      requestBody
    }: {
      sessionFormatId: SessionFormat['id'];
      requestBody: SessionFormatCreateRequest;
    }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatUpdate({ sessionFormatId, requestBody });
    },

    deleteFormat({ sessionFormatId }: { sessionFormatId: SessionFormat['id'] }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatDelete({ sessionFormatId });
    },

    deleteGlobalFormat({ sessionFormatId }: { sessionFormatId: SessionFormat['id'] }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatDelete({ sessionFormatId });
    },

    reorderFormats({ requestBody }: { requestBody: SessionFormatReorder }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatReorder({ requestBody });
    },

    setDefaultFormat({ sessionFormatId }: { sessionFormatId: SessionFormat['id'] }) {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatSetDefault({ sessionFormatId });
    },

    unsetDefaultFormat() {
      const apiStore = useApiStore();

      return apiStore.api.sessionFormats.sessionFormatUnsetDefault();
    }
  },
  persist: {
    storage: persistedState.localStorage,
    omit: ['loading']
  }
});
